/**
 * One check, many jobs (#1916) — the words and the client-side arithmetic.
 *
 * Built to the approved mockup "One Check, Many Jobs" (Corbin 10/16). React-
 * free so it can be tested without mounting anything. The SERVER owns the
 * real rules (it re-reads every balance inside its transaction); this is what
 * Regina sees before she saves, and on a money screen wrong words or a wrong
 * "Left" number are their own bug. Every amount here is integer cents.
 *
 * Seventh-grade words. Meaning from colour, position and big numbers first.
 */

export function centsOf(dollars: string | number | null | undefined): number {
    if (dollars === null || dollars === undefined || dollars === "") return 0;
    const n = typeof dollars === "number" ? dollars : Number(String(dollars).replace(/[$,\s]/g, ""));
    if (!Number.isFinite(n)) return 0;
    return Math.round(n * 100);
}

export function money(cents: number): string {
    const sign = cents < 0 ? "-" : "";
    return `${sign}$${(Math.abs(cents) / 100).toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    })}`;
}

export const toDollars = (cents: number) => Math.round(cents) / 100;

export type Method = "check" | "ach" | "cash" | "card_elsewhere" | "card_on_file" | "card_pay_link" | "credit";

/** The six doors on "How they paid", in the order the office meets them. */
export const METHOD_CHOICES: Array<{ key: Method; label: string; hint: string }> = [
    { key: "check", label: "Check", hint: "A paper check" },
    { key: "ach", label: "ACH / bank", hint: "Money sent bank to bank" },
    { key: "card_on_file", label: "Charge a card on file", hint: "We charge their saved card once" },
    { key: "card_pay_link", label: "Send one pay link", hint: "They pay every invoice with one link" },
    { key: "card_elsewhere", label: "Card (already charged)", hint: "Taken on the terminal or by phone" },
    { key: "cash", label: "Cash", hint: "" },
];

export const METHOD_LABEL: Record<Method, string> = {
    check: "Check",
    ach: "ACH / bank",
    cash: "Cash",
    card_elsewhere: "Card (already charged)",
    card_on_file: "Card on file",
    card_pay_link: "Pay link",
    credit: "Customer credit",
};

/** A charged card or a pay link: the amount is what the invoices add up to, not typed. */
export function amountIsTyped(m: Method): boolean {
    return m !== "card_on_file" && m !== "card_pay_link";
}

export function needsReference(m: Method): boolean {
    return m === "check" || m === "ach" || m === "card_elsewhere";
}

export function referenceLabel(m: Method): string {
    if (m === "check") return "Check #";
    if (m === "ach") return "ACH trace / reference";
    if (m === "card_elsewhere") return "Card reference (terminal receipt #)";
    return "Reference";
}

export const SHORT_REASONS = [
    { key: "price_dispute", label: "Price dispute" },
    { key: "tax_not_paid", label: "Tax not paid" },
    { key: "city_fee_not_paid", label: "City fee not paid" },
    { key: "late_fee_not_paid", label: "Late fee not paid" },
    { key: "our_price_wrong", label: "Our price was wrong" },
    { key: "other", label: "Other" },
] as const;

export function shortReasonLabel(key: string | null | undefined): string | null {
    if (!key) return null;
    return SHORT_REASONS.find((r) => r.key === key)?.label ?? key;
}

/** An open invoice as the workspace sends it. */
export interface WsInvoice {
    id: string;
    customer_id: string;
    customer_name: string | null;
    invoice_number: string | null;
    status: string;
    total: number;
    balance: number;
    paid_so_far: number;
    created_at: string;
    due_date: string | null;
    is_st: boolean;
    in_qbo: boolean;
    qbo_owes: number | null;
    job_id: string | null;
    wo_number: string | null;
    customer_po: string | null;
    site_name: string | null;
    site_city: string | null;
    blocked: string | null;
    on_dispute: boolean;
}

/** One ticked invoice on the screen. */
export interface LineState {
    invoiceId: string;
    /** "full" = what it owes now; "part" = a typed amount with a reason. */
    mode: "full" | "part";
    partCents: number;
    shortReason: string | null;
    shortNote: string;
}

export function lineCents(line: LineState, inv: WsInvoice | undefined): number {
    if (!inv) return 0;
    const owes = centsOf(inv.balance);
    if (line.mode === "full") return owes;
    return Math.max(0, Math.min(line.partCents, owes));
}

/** QuickBooks (last night) says it owes less than we'd put on it — the design's red stop. */
export function qboSaysPaid(inv: WsInvoice, cents: number): boolean {
    return inv.qbo_owes !== null && cents > centsOf(inv.qbo_owes);
}

export type LineTone = "ok" | "warn" | "bad";

/** What the row says, and its colour. */
export function lineVerdict(line: LineState, inv: WsInvoice): { tone: LineTone; text: string } {
    const cents = lineCents(line, inv);
    const owes = centsOf(inv.balance);
    if (qboSaysPaid(inv, cents)) {
        return {
            tone: "bad",
            text: `QuickBooks says this owes ${money(centsOf(inv.qbo_owes))}. Leave it off and send it to Corbin.`,
        };
    }
    if (line.mode === "part") {
        if (cents <= 0) return { tone: "bad", text: "Type what they paid on this one." };
        if (cents < owes && !line.shortReason)
            return { tone: "bad", text: `Short ${money(owes - cents)}. Pick a reason.` };
        if (line.shortReason === "other" && !line.shortNote.trim())
            return { tone: "bad", text: 'Say what "Other" is.' };
        if (cents < owes)
            return { tone: "warn", text: `Short ${money(owes - cents)} · ${shortReasonLabel(line.shortReason)}` };
    }
    if (inv.status === "draft")
        return { tone: "warn", text: "Draft, never sent. It will be opened first (no email goes out)." };
    return { tone: "ok", text: "Paid in full" };
}

export interface Totals {
    checkCents: number;
    appliedCents: number;
    creditCents: number;
    leftCents: number;
    lineCount: number;
    fullCount: number;
    shortCount: number;
    badCount: number;
}

export function totals(
    checkCents: number,
    lines: readonly LineState[],
    byId: ReadonlyMap<string, WsInvoice>,
    keepAsCredit: boolean,
    typed: boolean,
): Totals {
    let applied = 0;
    let full = 0;
    let short = 0;
    let bad = 0;
    for (const l of lines) {
        const inv = byId.get(l.invoiceId);
        if (!inv) continue;
        const c = lineCents(l, inv);
        applied += c;
        const v = lineVerdict(l, inv);
        if (v.tone === "bad") bad++;
        if (c < centsOf(inv.balance)) short++;
        else full++;
    }
    const check = typed ? checkCents : applied;
    const rawLeft = check - applied;
    const credit = keepAsCredit && rawLeft > 0 ? rawLeft : 0;
    return {
        checkCents: check,
        appliedCents: applied,
        creditCents: credit,
        leftCents: rawLeft - credit,
        lineCount: lines.length,
        fullCount: full,
        shortCount: short,
        badCount: bad,
    };
}

/** Why "Review and save" is off, in one short line. Null = it's on. */
export function saveBlocker(
    t: Totals,
    opts: { method: Method; reference: string; creditReason: string; duplicate: boolean; dateOk: boolean },
): string | null {
    if (opts.duplicate) return "This number is already saved. Open that one or fix the number.";
    if (t.lineCount === 0) return "Tick at least one invoice.";
    if (amountIsTyped(opts.method) && t.checkCents <= 0) return "Type the amount on the check.";
    if (needsReference(opts.method) && !opts.reference.trim()) {
        return opts.method === "check" ? "Type the check number." : "Type the reference number.";
    }
    if (!opts.dateOk) return "Pick the date on the check (not in the future).";
    if (t.badCount > 0) return `${t.badCount} invoice${t.badCount === 1 ? " needs" : "s need"} a look (red).`;
    if (t.leftCents > 0) return `${money(t.leftCents)} is left. Tick more invoices or keep it as a credit.`;
    if (t.leftCents < 0) return `That's ${money(-t.leftCents)} more than the check.`;
    if (t.creditCents > 0 && opts.creditReason.trim().length < 3) return "Say why the leftover is kept as a credit.";
    return null;
}

/** Widths of the three gauge pieces, as percentages of the check. */
export function gauge(t: Totals): { applied: number; credit: number; over: number } {
    const base = Math.max(t.checkCents, t.appliedCents, 1);
    const pct = (c: number) => Math.max(0, Math.min(100, (c / base) * 100));
    return {
        applied: pct(Math.min(t.appliedCents, t.checkCents || t.appliedCents)),
        credit: pct(t.creditCents),
        over: t.appliedCents > t.checkCents && t.checkCents > 0 ? pct(t.appliedCents - t.checkCents) : 0,
    };
}

/** "Cintas Fire National Account · #2008419152 · Oct 16" */
export function paymentTitle(p: {
    payer_name: string | null;
    method: string;
    reference: string | null;
    received_on: string;
}) {
    const ref =
        p.method === "check" && p.reference
            ? `Check #${p.reference.replace(/^#/, "")}`
            : p.reference
              ? p.reference
              : ((METHOD_LABEL as Record<string, string>)[p.method] ?? p.method);
    return [p.payer_name, ref, shortDay(p.received_on)].filter(Boolean).join(" · ");
}

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/** "Oct 16" / "Oct 16, 2025" from a YYYY-MM-DD, no timezone drift. */
export function shortDay(ymd: string | null | undefined, withYear = false): string {
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(ymd ?? "");
    if (!m) return "";
    return `${MONTHS[Number(m[2]) - 1]} ${Number(m[3])}${withYear ? `, ${m[1]}` : ""}`;
}

/** "6:00 PM" today, or "6:00 PM tomorrow". */
export function pushTimeLabel(iso: string | null | undefined, now = new Date()): string {
    if (!iso) return "";
    const d = new Date(iso);
    const day = (x: Date) => x.toLocaleDateString("en-US", { timeZone: "America/Chicago" });
    const time = d.toLocaleTimeString("en-US", { timeZone: "America/Chicago", hour: "numeric", minute: "2-digit" });
    if (day(d) === day(now)) return `today at ${time}`;
    const tomorrow = new Date(now.getTime() + 86_400_000);
    if (day(d) === day(tomorrow)) return `tomorrow at ${time}`;
    return `${d.toLocaleDateString("en-US", { timeZone: "America/Chicago", month: "short", day: "numeric" })} at ${time}`;
}

/** QuickBooks state of one account's payment, in words + tone. */
export function qboStateLabel(status: string): { text: string; tone: LineTone } {
    switch (status) {
        case "sent":
            return { text: "In QuickBooks", tone: "ok" };
        case "waiting":
            return { text: "Waiting for 6 PM", tone: "warn" };
        case "held":
            return { text: "Held — not sent", tone: "bad" };
        case "skipped":
            return { text: "Not sent (nothing for QuickBooks)", tone: "ok" };
        case "removed":
            return { text: "Taken back out of QuickBooks", tone: "warn" };
        default:
            return { text: status, tone: "warn" };
    }
}

/** A row's place / WO line: "7-Eleven #38262 · Grand Prairie · WO 8000243811". */
export function whereLine(inv: Pick<WsInvoice, "site_name" | "site_city" | "wo_number" | "customer_po">): string {
    const wo = inv.wo_number ?? inv.customer_po;
    return [inv.site_name, inv.site_city, wo ? `WO ${wo}` : null].filter(Boolean).join(" · ");
}

/** Oldest first, payer's own account first, then by account name. */
export function groupByAccount(
    invoices: readonly WsInvoice[],
    payerId: string,
): Array<{ customerId: string; name: string | null; invoices: WsInvoice[] }> {
    const map = new Map<string, { customerId: string; name: string | null; invoices: WsInvoice[] }>();
    for (const inv of invoices) {
        let g = map.get(inv.customer_id);
        if (!g) {
            g = { customerId: inv.customer_id, name: inv.customer_name, invoices: [] };
            map.set(inv.customer_id, g);
        }
        g.invoices.push(inv);
    }
    return [...map.values()].sort((a, b) =>
        a.customerId === payerId ? -1 : b.customerId === payerId ? 1 : (a.name ?? "").localeCompare(b.name ?? ""),
    );
}
